<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import FosInput from './FosInput.vue'
import { parseCoordinates, formatCoordinates } from '@/utils/coordinates'

const props = defineProps<{
  lat: number | null
  lng: number | null
  readonly?: boolean
  label?: string
}>()

const emit = defineEmits<{
  'update:coords': [coords: { lat: number | null; lng: number | null }]
}>()

const inputText = ref('')
const parseError = ref(false)
const focused = ref(false)

watchEffect(() => {
  if (focused.value) return
  inputText.value =
    props.lat !== null && props.lng !== null ? formatCoordinates(props.lat, props.lng) : ''
})

function onFocus() {
  focused.value = true
  parseError.value = false
}

function onBlur() {
  focused.value = false
  const raw = inputText.value.trim()

  if (raw === '') {
    parseError.value = false
    emit('update:coords', { lat: null, lng: null })
    return
  }

  const result = parseCoordinates(raw)
  if (result) {
    parseError.value = false
    emit('update:coords', result)
  } else {
    parseError.value = true
  }
}
</script>

<template>
  <div class="flex flex-col gap-0.5">
    <label class="text-[10px] text-outline font-label uppercase tracking-widest">
      {{ label ?? 'Coordinates' }}
    </label>

    <template v-if="readonly">
      <span class="text-on-surface-variant text-sm font-mono">
        {{ lat !== null && lng !== null ? formatCoordinates(lat, lng) : '—' }}
      </span>
    </template>

    <template v-else>
      <div :class="parseError ? 'outline outline-1 outline-error rounded' : ''">
        <FosInput
          v-model="inputText"
          placeholder="37.8749, -122.4194 or 10S 551449 4182058"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>
      <span v-if="parseError" class="text-[10px] text-error font-mono">
        Unrecognized format. Try "37.8749, -122.4194" or "10S 551449 4182058"
      </span>
    </template>
  </div>
</template>
